import React, { useLayoutEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import { Typography, Spin } from "antd";

import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

import { useDeepCompareMemoize } from "../../../hooks/useDeepCompareMemoize";

const { Title, Text } = Typography;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 4px 4px 12px #ccc;
  padding: 20px;
  background: #fff;
`;

const StyledTitle = styled(Title)`
  padding-bottom: 2px;
  border-bottom: 2px solid #eee;
`;

const Chart = styled.div`
  height: 350px;
`;

const ReportPieChart = ({
  title,
  id,
  data,
  fields,
  value,
  category,
  legendPosition,
  loading,
}) => {
  const rootRef = useRef(null);

  const stableData = useDeepCompareMemoize(data);
  const stableFields = useDeepCompareMemoize(fields);

  const hasData = stableData && Object.keys(stableData).length > 0;

  const mappedData = useMemo(() => {
    if (!hasData) return [];
    return Object.entries(stableData)
      .filter(([k]) => stableFields.includes(k))
      .map(([k, v]) => ({ status: k, cases: v }));
  }, [hasData, stableData, stableFields]);

  useLayoutEffect(() => {
    if (!hasData) return undefined;

    const root = am5.Root.new(id);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: legendPosition === "bottom" ? root.verticalLayout : root.horizontalLayout,
        innerRadius: 0,
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: value,
        categoryField: category,
        alignLabels: false,
      })
    );

    series.labels.template.setAll({
      text: "{valuePercentTotal.formatNumber('#.0')}% ({value})",
      fill: am5.color(0xffffff),
      radius: -35,
      inside: true,
    });
    series.ticks.template.set("forceHidden", true);

    series.data.setAll(mappedData);

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: legendPosition === "bottom" ? am5.percent(50) : 0,
        x: legendPosition === "bottom" ? am5.percent(50) : 0,
        marginTop: 15,
        marginBottom: 15,
        layout: root.verticalLayout,
      })
    );
    legend.valueLabels.template.setAll({
      text: "{valuePercentTotal.formatNumber('#.0')}% ({value})",
      paddingLeft: 30,
    });
    legend.data.setAll(series.dataItems);

    series.appear(800, 100);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [hasData, mappedData, id, value, category, legendPosition]);

  return (
    <Container>
      <StyledTitle level={4}>{title}</StyledTitle>
      {loading && <Spin />}
      {hasData ? (
        <Chart id={id} style={{ textTransform: "capitalize" }} />
      ) : (
        !loading && <Text>No Data</Text>
      )}
    </Container>
  );
};

export default ReportPieChart;
